import { useEffect, useRef, useState } from 'react';

import { Spinner } from './ui/spinner';

interface InfiniteScrollProps {
  loading: boolean;
  /** Ensure this is properly memoized or it will trigger an infinite loop */
  loadPage: () => void;
  children?: React.ReactNode;
}

export const InfiniteScrollSpinner = ({ loading }: { loading: boolean }) => {
  return (
    <div className="flex items-center justify-center py-4">
      <Spinner showSpinner={loading} />
    </div>
  );
};

export const useInfiniteScroll = ({ loading, loadPage }: InfiniteScrollProps) => {
  const [loadingSentinel, setLoadingSentinel] = useState<HTMLDivElement | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // This has to be done in a useEffect because IntersectionObserver
    // values can't be created on the server
    observer.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        loadPage();
      }
    });
  }, [loadPage]);

  useEffect(() => {
    // The current observer has to be stored in a variable so both the
    // setup and cleanup functions reference the same value
    const currentObserver = observer.current;
    if (loadingSentinel) {
      currentObserver?.observe(loadingSentinel);
    }
    return () => {
      if (loadingSentinel) {
        currentObserver?.unobserve(loadingSentinel);
      }
    };
    // loadPage is an indirect dependency since it's closed over by
    // the IntersectionObserver callback. If its value changes, the
    // IntersectionObserver needs to be reinitialized with a closure
    // over the new value.
  }, [loadingSentinel, loadPage]);

  return {
    loadingSentinel: <div ref={setLoadingSentinel} />,
    spinner: <InfiniteScrollSpinner loading={loading} />,
  };
};
